import { Link } from "react-router";
import { ArrowLeft, Activity, Cloud, BarChart3, HardDrive } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";
import { Mermaid } from "@/components/shared/MermaidDiagram";

const PHOLIS_DATA_FLOW_DIAGRAM = `
flowchart LR
  subgraph Input["Blob Storage"]
    data[Measurement data]
    specs[Specs]
    masks[Masks]
  end

  pholis[Pholis Service]

  subgraph Output["Blob Storage"]
    results[Result files]
  end

  subgraph Viz["Visualization"]
    gf[Grafana]
  end

  data .-> pholis
  specs .-> pholis
  masks .-> pholis

  db[Pholis Database]

  pholis --> results
  pholis --> db
  db --> gf
`;

export function PholisDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Pholis</h1>
              <p className="text-xl text-gray-600">Photonics data analysis service</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>What is Pholis?</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              Pholis is an analytical reporting tool for photonics data measured on optical testers.
            </p>
          </CardContent>
        </Card>

        {/* Data Flow Diagram */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Data Flow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Mermaid chart={PHOLIS_DATA_FLOW_DIAGRAM} />
          </CardContent>
        </Card>

        {/* Inputs */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-600" />
              Inputs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">Pholis requires three types of input files:</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <HardDrive className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Measurement data</p>
                  <p className="text-sm text-gray-600">
                    Raw photonics measurement data for the lot being analyzed.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <HardDrive className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Specs</p>
                  <p className="text-sm text-gray-600">
                    Spec files defining analysis parameters and acceptance criteria.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <HardDrive className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Masks</p>
                  <p className="text-sm text-gray-600">
                    Mask files describing the spatial layout of the measurement structures.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-600" />
              Outputs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">Each analysis produces two outputs:</p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <HardDrive className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Analysis result files</p>
                  <p className="text-sm text-gray-600">
                    The full analysis output stored in blob storage, grouped per lot.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border">
                <HardDrive className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">Database records</p>
                  <p className="text-sm text-gray-600">
                    Structured results persisted to the database for trend analysis and
                    visualization.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grafana Visualization */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Visualization
            </CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              Analysis results are visualized in <strong>Grafana</strong>, which provides dashboards
              for trend analysis across lots. Grafana reads directly from the database and is
              available as part of Photon Flow.
            </p>
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
